import React, { useEffect, useState } from "react";
import { supabase } from "../../supabase-client";
import PedidoModal from "../../components/modal/PedidoModal"; // 👈 Import the modal
import {
   Eye,
   CheckCircle,
   XCircle,
   Clock,
   Package,
   Truck,
   DollarSign,
} from "lucide-react";

export default function Pedidos() {
   const [pedidos, setPedidos] = useState([]);
   const [loading, setLoading] = useState(true);
   const [selectedPedido, setSelectedPedido] = useState(null);
   const [filterEstado, setFilterEstado] = useState("todos");

   // Estados disponibles
   const estados = {
      pendiente: {
         label: "Pendiente",
         color: "bg-yellow-100 text-yellow-800",
         icon: Clock,
      },
      confirmado: {
         label: "Confirmado",
         color: "bg-blue-100 text-blue-800",
         icon: CheckCircle,
      },
      pagado: {
         label: "Pagado",
         color: "bg-blue-100 text-blue-800",
         icon: DollarSign,
      },
      procesando: {
         label: "Procesando",
         color: "bg-purple-100 text-purple-800",
         icon: Package,
      },
      enviado: {
         label: "Enviado",
         color: "bg-green-100 text-green-800",
         icon: Truck,
      },
      entregado: {
         label: "Entregado",
         color: "bg-green-100 text-green-800",
         icon: CheckCircle,
      },
      cancelado: {
         label: "Cancelado",
         color: "bg-red-100 text-red-800",
         icon: XCircle,
      },
   };

   // Fetch pedidos from database
   const fetchPedidos = async () => {
      setLoading(true);
      try {
         const { data, error } = await supabase
            .from("pedidos")
            .select(
               `
               *,
               usuarios (
                  id,
                  nombre,
                  email,
                  documento,
                  celular
               )
            `
            )
            .order("fecha_pedido", { ascending: false });

         if (error) {
            console.error("Error fetching pedidos:", error);
            alert("Error al cargar los pedidos");
            return;
         }

         setPedidos(data || []);
      } catch (err) {
         console.error("Error:", err);
         alert("Error al conectar con la base de datos");
      }
      setLoading(false);
   };

   // Update pedido estado
   const updateEstado = async (pedidoId, nuevoEstado) => {
      try {
         const isCancelado =
            String(nuevoEstado).toLowerCase() === "cancelado" ||
            String(nuevoEstado) === "Cancelado";

         if (isCancelado) {
            const confirmCancelacion = window.confirm(
               "Este cambio es irreversible: al cancelar el pedido, las obras se liberarán nuevamente. ¿Deseás continuar?"
            );

            if (!confirmCancelacion) {
               return;
            }
         }

         const { error } = await supabase
            .from("pedidos")
            .update({
               estado: nuevoEstado,
               updated_at: new Date().toISOString(),
            })
            .eq("id", pedidoId);

         if (error) {
            console.error("Error updating estado:", error);
            alert("Error al actualizar el estado");
            return;
         }

         if (isCancelado) {
            const pedidoActual = pedidos.find((pedido) => pedido.id === pedidoId);
            let obraIds = [];

            if (pedidoActual?.obras) {
               try {
                  const obrasParsed =
                     typeof pedidoActual.obras === "string"
                        ? JSON.parse(pedidoActual.obras)
                        : pedidoActual.obras;

                  if (Array.isArray(obrasParsed)) {
                     obraIds = obrasParsed
                        .map((obra) => Number(obra?.id))
                        .filter((id) => Number.isFinite(id));
                  }
               } catch (parseError) {
                  console.error("Error parsing pedido.obras:", parseError);
               }
            }

            if (obraIds.length > 0) {
               const { error: obrasError } = await supabase
                  .from("obras")
                  .update({ estado: 2 })
                  .in("id", obraIds);

               if (obrasError) {
                  console.error("Error updating obras to estado 2:", obrasError);
                  alert("El pedido se canceló, pero hubo un error al liberar las obras");
               }
            }
         }

         // Update local state
         setPedidos((prev) =>
            prev.map((pedido) =>
               pedido.id === pedidoId
                  ? { ...pedido, estado: nuevoEstado }
                  : pedido
            )
         );

         // Update pedidos history
         try {
            const { error } = await supabase.from("pedidos_hist").insert({
               pedido_id: pedidoId,
               estado: nuevoEstado,
            });
         } catch (error) {
            console.error("Error in insertPedidosHist:", error);
         }

      } catch (err) {
         console.error("Error:", err);
         alert("Error al actualizar el estado");
      }
   };

   // Filter pedidos by estado
   const filteredPedidos =
      filterEstado === "todos"
         ? pedidos
         : pedidos.filter((pedido) => pedido.estado === filterEstado);

   // Format date
   const formatDate = (dateString) => {
      return new Date(dateString).toLocaleString("es-AR", {
         year: "numeric",
         month: "2-digit",
         day: "2-digit",
         hour: "2-digit",
         minute: "2-digit",
      });
   };

   useEffect(() => {
      if (pedidos){
         fetchPedidos();
      }
   }, []);

   if (loading) {
      return (
         <div className="p-6 text-center">
            <h1 className="text-3xl font-bold mb-4">Pedidos</h1>
            <div className="flex items-center justify-center">
               <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
               <span className="ml-2">Cargando pedidos...</span>
            </div>
         </div>
      );
   }

   return (
      <div className="p-6 max-w-7xl mx-auto">
         <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold">Gestión de Pedidos</h1>
            <button
               onClick={fetchPedidos}
               className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
               🔄 Actualizar
            </button>
         </div>

         {/* Statistics */}
         <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg text-center">
               <div className="text-2xl font-bold text-gray-700">
                  {pedidos.length}
               </div>
               <div className="text-sm text-gray-600">Total</div>
            </div>
            {Object.entries(estados).map(([key, config]) => {
               const count = pedidos.filter((p) => p.estado === key).length;
               return (
                  <div
                     key={key}
                     className="bg-gray-50 p-4 rounded-lg text-center"
                  >
                     <div className="text-2xl font-bold text-gray-700">
                        {count}
                     </div>
                     <div className="text-sm text-gray-600">{config.label}</div>
                  </div>
               );
            })}
         </div>

         {/* Filters */}
         <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
               Filtrar por estado:
            </label>
            <select
               value={filterEstado}
               onChange={(e) => setFilterEstado(e.target.value)}
               className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
               <option value="todos">Todos los estados</option>
               {Object.entries(estados).map(([key, config]) => (
                  <option key={key} value={key}>
                     {config.label}
                  </option>
               ))}
            </select>
         </div>

         {/* Pedidos Table */}
         {filteredPedidos.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
               {filterEstado === "todos"
                  ? "No hay pedidos registrados"
                  : `No hay pedidos con estado: ${estados[filterEstado]?.label}`}
            </div>
         ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
               <table className="w-full">
                  <thead className="bg-gray-50">
                     <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                           ID
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                           Fecha Pedido
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                           Cliente
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                           Total
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                           Estado
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                           Acciones
                        </th>
                     </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                     {filteredPedidos.map((pedido) => {
                        const estadoConfig =
                           estados[pedido.estado] || estados.pendiente;
                        const IconComponent = estadoConfig.icon;

                        return (
                           <tr key={pedido.id} className="hover:bg-gray-50">
                              <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                 #{pedido.id}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                 {formatDate(pedido.fecha_pedido)}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                 <div className="text-sm font-medium text-gray-900">
                                    {pedido.usuarios?.nombre || "N/A"}
                                 </div>
                                 <div className="text-sm text-gray-500">
                                    {pedido.usuarios?.email || "N/A"}
                                 </div>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                                 ${Number(pedido.total || 0).toLocaleString()}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                 <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${estadoConfig.color}`}
                                 >
                                    <IconComponent className="w-3 h-3 mr-1" />
                                    {estadoConfig.label}
                                 </span>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                 <div className="flex space-x-2">
                                    <button
                                       onClick={() => setSelectedPedido(pedido)}
                                       className="text-blue-600 hover:text-blue-900 transition-colors"
                                       title="Ver detalles"
                                    >
                                       <Eye className="w-4 h-4" />
                                    </button>
                                    <select
                                       value={pedido.estado}
                                       onChange={(e) =>
                                          updateEstado(
                                             pedido.id,
                                             e.target.value
                                          )
                                       }
                                       className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    >
                                       {Object.entries(estados).map(
                                          ([key, config]) => (
                                             <option key={key} value={key}>
                                                {config.label}
                                             </option>
                                          )
                                       )}
                                    </select>
                                 </div>
                              </td>
                           </tr>
                        );
                     })}
                  </tbody>
               </table>
            </div>
         )}

         {/* 👈 Use the separated Modal Component */}
         <PedidoModal 
            pedido={selectedPedido}
            onClose={() => setSelectedPedido(null)}
         />
      </div>
   );
}